import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { useViewHealthMarker } from '@/lib/view-health';

import { ActiveCampaignIcon } from './ActiveCampaignIcon';
import { ActiveCampaignSetupWizard } from './components/ActiveCampaignSetupWizard';
import { ConnectionStatusPanel } from './components/ConnectionStatusPanel';
import { useActiveCampaignStatus } from './hooks/useActiveCampaignStatus';

const LAUNCHER_TILE_CLASS =
  'flex h-12 w-12 items-center justify-center rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs';

export function ActiveCampaignSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  useViewHealthMarker('settings-section-ready', { ready: true });

  const status = useActiveCampaignStatus(orgId);
  const connected = status.data?.connected ?? false;

  const { refetch } = status;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const [wizardOpen, setWizardOpen] = useState(false);

  const openWizard = useCallback(() => {
    setWizardOpen(true);
  }, []);

  const handleWizardOpenChange = useCallback((open: boolean) => {
    setWizardOpen(open);
  }, []);

  // Where the wizard resumes from when (re)opened.
  const defaultStepId = connected ? 'fields' : 'connect';

  let bodyContent: ReactNode = null;
  if (status.isLoading) {
    bodyContent = (
      <div className='flex justify-center py-4'>
        <LoadingIndicator size='sm' />
      </div>
    );
  } else if (status.isError) {
    // Only a real query error is a failure. Idle state (no org yet, query
    // disabled) also leaves data undefined without isLoading — don't treat that
    // as an error, or the banner flashes while org context is still resolving.
    bodyContent = (
      <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
        <p className='text-sm font-semibold text-utility-error-900'>
          Couldn&apos;t load ActiveCampaign status
        </p>
        <p className='mt-1 text-sm text-utility-error-700'>
          Something went wrong while checking your connection. Please try again.
        </p>
        <div className='mt-3'>
          <Button color='secondary' size='sm' onClick={handleRetry}>
            Retry
          </Button>
        </div>
      </div>
    );
  } else if (connected && orgId !== undefined) {
    bodyContent = <ConnectionStatusPanel orgId={orgId} />;
  } else if (isAdmin && orgId !== undefined) {
    bodyContent = (
      <div className='max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6'>
        <div className='flex items-start gap-4'>
          <span className={LAUNCHER_TILE_CLASS}>
            <ActiveCampaignIcon className='h-6 w-6 text-[var(--color-text-primary)]' />
          </span>
          <div className='flex-1'>
            <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
              Connect your ActiveCampaign account
            </h3>
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              Keep an ActiveCampaign account in two-way sync with your Ahoy
              contacts.
            </p>
            <div className='mt-4'>
              <Button color='primary' size='md' onClick={openWizard}>
                Set up ActiveCampaign
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!isAdmin && orgId !== undefined) {
    // Gate the non-admin warning on a resolved org (like the branches above). Without this it
    // flashes while org context is still loading — isAdmin is false before permissions resolve.
    bodyContent = (
      <div className='rounded-lg border border-utility-warning-200 bg-utility-warning-50 p-4'>
        <p className='text-sm text-utility-warning-700'>
          ActiveCampaign can only be connected by an account admin.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      <header>
        <h2 className='text-lg font-semibold text-fg-primary'>
          ActiveCampaign
        </h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Sync your ActiveCampaign contacts with Ahoy.
        </p>
      </header>

      <div className='space-y-6'>
        {bodyContent}

        {/* Always mounted for admins so reopening the wizard after connecting
            still lands on the fields step instead of losing that context. */}
        {isAdmin && orgId !== undefined && (
          <ActiveCampaignSetupWizard
            orgId={orgId}
            isOpen={wizardOpen}
            onOpenChange={handleWizardOpenChange}
            defaultStepId={defaultStepId}
          />
        )}
      </div>
    </div>
  );
}
